import React, { useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { Select, Input, Radio, Button, Modal, Avatar, message } from "antd";
import { PlusOutlined, ArrowLeftOutlined } from "@ant-design/icons";
import DashboardView from "../DashboardView";
import AddBoardGroup from "../../components/BoardGoups/AddBoardGroup";
import { addDashboardConfig } from "../../redux/editorSlice";
import { getGroupsAsync } from "../../redux/boardSlice";
// Organization is typed in by the user now. Kept in case we go back to a fixed list.
// import { ORG_LIST } from "../../constants/organizations";
import { reportAppIdFor } from "../../utils/appAliases";
import PrivateIcon from "../../assets/icons/lock.svg";
import PublicIcon from "../../assets/icons/public.svg";
import GlobalIcon from "../../assets/icons/global.svg";
import "./CreateDashboard.css";

// Kept as its own component so DashboardView can clone it with its onAddGroup/onAddResource props.
function CreateBoardForm() {
  const dispatch = useDispatch();
  const history = useHistory();
  const addBoardGroupRef = React.useRef();

  const boards = useSelector((state) => state.boards);
  const user = useSelector((state) => state.user);

  const [applicationId, setApplicationId] = useState(undefined);
  const [boardName, setBoardName] = useState("");
  // 1 = Private, 2 = Public, 3 = Global (special users only) - same values as the old modal
  const [accessType, setAccessType] = useState(1);
  const [organization, setOrganization] = useState("");
  // const [orgIdList, setOrgIdList] = useState([]); // used by the old ORG_LIST dropdown
  const [selectedGroupId, setSelectedGroupId] = useState(undefined);
  const [selectedSubGroupId, setSelectedSubGroupId] = useState("none");
  const [boardType, setBoardType] = useState("dashboard");
  const [isGroupModalVisible, setIsGroupModalVisible] = useState(false);
  // a field only shows its error once the user has interacted with it (or tried to submit)
  const [touched, setTouched] = useState({});

  const appList = (user.apps && user.apps.Result) || [];
  const users = user.otherUsers || [];
  const isSpecialUser = localStorage.getItem("IsSpecialUser") === "true";
  /* Same as the old modal (sharedListShow): the org field only belongs to a Private board.
     Hidden for everyone but a special user until it becomes a proper picker - right now it
     is free text, and an organisation has to be chosen by id, which nobody else can do. */
  const isOrgFieldShown = accessType === 1 && isSpecialUser;

  const groupList = useMemo(() => {
    const groups = boards.groups || [];
    /* Groups no longer carry a Dashboard/Report type, so the list is not narrowed by
       the board type any more - every group is offered whichever type is being created. */
    // const type = boardType === "reports" ? "report" : "dashboard";
    return (
      groups
        // .filter((gd) => gd.groupType === type)
        .filter((gd) => (accessType === 3 ? gd.isGlobal : !gd.isGlobal))
        .sort((a, b) => (a.title < b.title ? -1 : a.title > b.title ? 1 : 0))
    );
  }, [boards.groups, accessType]);

  const selectedGroup = groupList.find((g) => g.id === selectedGroupId);

  // Required fields: Application Name, Name and Group
  const missing = {
    application: !applicationId,
    boardName: !boardName.trim(),
    group: !selectedGroupId,
  };
  const isFormValid = !missing.application && !missing.boardName && !missing.group;
  const showError = (field) => touched[field] && missing[field];
  const markTouched = (field) => setTouched((prev) => ({ ...prev, [field]: true }));

  const onChangeBoardType = (value) => {
    setBoardType(value);
    /* The group list used to differ per board type, so the previous pick had to be
       cleared. It no longer does, so a chosen group survives switching the type. */
    // setSelectedGroupId(undefined);
    // setSelectedSubGroupId("none");
  };

  const onChangeAccessType = (e) => {
    setAccessType(e.target.value);
    setSelectedGroupId(undefined);
    setSelectedSubGroupId("none");
  };

  const onCloseGroupModal = () => {
    if (addBoardGroupRef.current && addBoardGroupRef.current.resetToInitialState) {
      addBoardGroupRef.current.resetToInitialState();
    }
    setIsGroupModalVisible(false);
    dispatch(getGroupsAsync(user?.user?.accessToken));
  };

  const handleCancel = () => {
    history.push("/");
  };

  const handleCreateBoard = () => {
    // the button is disabled while the form is invalid, this is just a safety net
    if (!isFormValid) {
      setTouched({ application: true, boardName: true, group: true });
      message.error("Please fill all the required fields");
      return;
    }

    const configData = {
      boardType: boardType,
      boardName: boardName.trim(),
      isPuplic: accessType === 2,
      // isPuplic: accessType === 1, // <- old modal behaviour (true when "Private" was picked)
      isGlobal: accessType === 3,
      shareWith: [],
      /* The application id reports are stored under, which is not always the one the
         user picked - see utils/appAliases.js. Writing the licensed id instead put new
         reports in a bucket neither this app's report list nor the ERP site reads. */
      appList: [reportAppIdFor(applicationId)],
      groupId: selectedGroupId,
      selectedSubGoupId: selectedSubGroupId,
      // the field is hidden for Public/Global, so don't send a leftover value
      organization: isOrgFieldShown ? organization.trim() : "",
      // sharedOrgIdList: orgIdList, // <- old ORG_LIST dropdown value
    };

    localStorage.setItem("currentDashboardConfig", JSON.stringify(configData));
    dispatch(addDashboardConfig(configData));
    history.push(boardType === "reports" ? "/editor/reports" : "/editor/dashboard");
  };

  return (
    <div className="create-board-page">
      <div className="create-board-page-header">
        <div>
          <h2>Create New Dashboard/Reports</h2>
          <p>Add a new dashboard or report to your workspace</p>
        </div>
        <span className="create-board-back" onClick={() => history.push("/")}>
          <ArrowLeftOutlined /> Back to Home
        </span>
      </div>

      <div className="create-board-card">
        <h3 className="create-board-card-title">Add New Dashboard / Report</h3>
        <div className="create-board-divider" />

        <div className="create-board-row">
          <div className="create-board-label">
            Application Name: <span className="create-board-required">*</span>
          </div>
          <div className="create-board-field">
            <Select
              showSearch
              className={`create-board-control${showError("application") ? " has-error" : ""}`}
              placeholder="Select Application"
              optionFilterProp="label"
              value={applicationId}
              onBlur={() => markTouched("application")}
              onChange={(value) => {
                setApplicationId(value);
                markTouched("application");
              }}
            >
              {appList.map((app) => (
                <Select.Option key={app.ApplicationID} value={app.ApplicationID} label={app.Name}>
                  <span className="create-board-app-option">
                    {app.AppImage ? <Avatar size={20} src={app.AppImage} /> : null}
                    {app.Name}
                  </span>
                </Select.Option>
              ))}
            </Select>
            {showError("application") ? <div className="create-board-error">Please select an application</div> : null}
          </div>
        </div>

        <div className="create-board-row">
          <div className="create-board-label">
            Name: <span className="create-board-required">*</span>
          </div>
          <div className="create-board-field">
            <Input
              className={`create-board-control${showError("boardName") ? " has-error" : ""}`}
              placeholder={boardType === "reports" ? "Report Name" : "Report or Dashboard Name"}
              value={boardName}
              onBlur={() => markTouched("boardName")}
              onChange={(e) => {
                setBoardName(e.target.value);
                markTouched("boardName");
              }}
            />
            {showError("boardName") ? (
              <div className="create-board-error">
                Please enter a {boardType === "reports" ? "report" : "dashboard"} name
              </div>
            ) : null}
          </div>
        </div>

        <div className="create-board-row">
          <div className="create-board-label" />
          <div className="create-board-field">
            <Radio.Group className="create-board-radios" value={accessType} onChange={onChangeAccessType}>
              <Radio value={1}>
                <img src={PrivateIcon} alt="private" /> <span>Private</span>
              </Radio>
              <Radio value={2}>
                <img src={PublicIcon} alt="public" /> <span>Public</span>
              </Radio>
              {isSpecialUser ? (
                <Radio value={3}>
                  <img src={GlobalIcon} alt="global" /> <span>Global</span>
                </Radio>
              ) : null}
            </Radio.Group>
          </div>
        </div>

        {/* Private boards only, and only for a special user - see isOrgFieldShown.
            The label goes with it, so nothing is left behind on the form. */}
        {isOrgFieldShown ? (
          <div className="create-board-row">
            <div className="create-board-label">Organization:</div>
            <div className="create-board-field">
              <Input
                className="create-board-control"
                placeholder="Enter Organization"
                value={organization}
                onChange={(e) => setOrganization(e.target.value)}
              />
              {/* Old hardcoded ORG_LIST dropdown - kept in case we need it back:
              <Select
                showSearch
                mode="multiple"
                className="create-board-control"
                placeholder="Select Organization"
                optionFilterProp="children"
                disabled={!isOrgListShown}
                value={orgIdList}
                onChange={setOrgIdList}
              >
                {ORG_LIST.map((org) => (
                  <Select.Option key={org.OrganisationID} value={org.OrganisationID}>
                    {org.Name}
                  </Select.Option>
                ))}
              </Select> */}
            </div>
          </div>
        ) : null}

        <div className="create-board-row">
          <div className="create-board-label">
            Group: <span className="create-board-required">*</span>
          </div>
          <div className="create-board-field">
            <div className="create-board-field-inline">
              <Select
                showSearch
                className={`create-board-control${showError("group") ? " has-error" : ""}`}
                placeholder="Select Group"
                optionFilterProp="children"
                value={selectedGroupId}
                onBlur={() => markTouched("group")}
                onChange={(value) => {
                  setSelectedGroupId(value);
                  setSelectedSubGroupId("none");
                  markTouched("group");
                }}
              >
                {groupList.map((gd) => (
                  <Select.Option key={gd.id} value={gd.id}>
                    {gd.title}
                  </Select.Option>
                ))}
              </Select>
              <button className="create-board-add-group" onClick={() => setIsGroupModalVisible(true)} type="button">
                <PlusOutlined />
              </button>
            </div>
            {showError("group") ? <div className="create-board-error">Please select a group</div> : null}
          </div>
        </div>

        {selectedGroup && selectedGroup.children && selectedGroup.children.length > 0 ? (
          <div className="create-board-row">
            <div className="create-board-label">Sub Group:</div>
            <div className="create-board-field">
              <Select
                showSearch
                className="create-board-control"
                placeholder="Select Sub Group"
                optionFilterProp="children"
                value={selectedSubGroupId}
                onChange={setSelectedSubGroupId}
              >
                <Select.Option key="no_sub" value="none">
                  None
                </Select.Option>
                {selectedGroup.children.map((c) => (
                  <Select.Option key={c.id} value={c.id}>
                    {c.title}
                  </Select.Option>
                ))}
              </Select>
            </div>
          </div>
        ) : null}

        <div className="create-board-row">
          <div className="create-board-label">Type:</div>
          <div className="create-board-field">
            <Select className="create-board-control" value={boardType} onChange={onChangeBoardType}>
              <Select.Option value="dashboard">Dashboard</Select.Option>
              <Select.Option value="reports">Report</Select.Option>
            </Select>
          </div>
        </div>

        <div className="create-board-divider" />

        <div className="create-board-footer">
          <Button className="create-board-cancel" onClick={handleCancel}>
            Cancel
          </Button>
          <Button type="primary" className="create-board-submit" disabled={!isFormValid} onClick={handleCreateBoard}>
            <PlusOutlined /> Create Board
          </Button>
        </div>
      </div>

      {/* Add Group Modal - same component the old flow used */}
      <Modal title="Add New Group" visible={isGroupModalVisible} onCancel={onCloseGroupModal} footer={null} width={720}>
        <AddBoardGroup
          ref={addBoardGroupRef}
          users={users}
          disableRedirect={true}
          handleCloseModal={onCloseGroupModal}
        />
      </Modal>
    </div>
  );
}

export default function CreateDashboard() {
  return (
    <DashboardView>
      <CreateBoardForm />
    </DashboardView>
  );
}
